import React, { ReactElement } from 'react';
import classNames from 'classnames';
import EmptyState from '@atlaskit/empty-state';
import Lozenge from '@atlaskit/lozenge';
import { Radio } from '@atlaskit/radio';
import { Box, Flex, Text } from '@atlaskit/primitives';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import {
  CreditCard,
  PaymentMethod,
  PaymentMethodType,
  generatedTypesNew,
  PaymentMethodTypeV2,
} from '@css-xp/graphtypes';
import { Ach, Maybe } from '@css-xp/graphtypes/src/generated-types-new';
import { isAchPaymentMethod } from '@css-xp/ppc-utils';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { TermPaymentMethod } from '../../term-payment-method';
import './payment-methods-table-v2.scss';
import { AchPanel } from '../../ach';

interface Props {
  cardPaymentMethods: PaymentMethod[];
  defaultPaymentMethodId: string | null;
  // eslint-disable-next-line no-unused-vars
  selectPaymentMethod: (id: string) => void;
  selectedPaymentMethodId: string;
  header?: string;
  setShowTermPaymentWarning?: (status: boolean) => void;
  isCreditManagementEnabled?: boolean;
  termPaymentMethods?: generatedTypesNew.PaymentOnTerms[];
  payPalPaymentMethods?: any;
  isTermPaymentAvailable?: boolean;
  isDefaultMethodIsCard?: boolean;
  termPaymentStatus?: Maybe<string> | null;
  version?: number;
  isPaypalSupportEnabled?: boolean;
  showHeader?: boolean;
  shouldShowACHPaymentMethod?: boolean;
  achPaymentMethods?: Ach[];
}

interface GetPaymentMethodOptionsArgs {
  paymentMethod: PaymentMethod | Ach;
  index: number;
  defaultPaymentMethodId: string | undefined | null;
  // eslint-disable-next-line no-unused-vars
  selectPaymentMethod: (id: string) => void;
  selectedPaymentMethodId: string;
  setShowTermPaymentWarning?: (status: boolean) => void;
  isCreditManagementEnabled?: boolean;
  isTermPaymentAvailable?: boolean;
  isDefaultMethodIsCard?: boolean;
  termPaymentStatus?: Maybe<string> | null;
  version?: number;
  shouldShowACHPaymentMethod?: boolean;
}

const getPaymentMethodOption = ({
  version,
  paymentMethod,
  index,
  defaultPaymentMethodId,
  selectPaymentMethod,
  selectedPaymentMethodId,
  setShowTermPaymentWarning,
  isCreditManagementEnabled,
  isTermPaymentAvailable,
  isDefaultMethodIsCard,
  termPaymentStatus,
  shouldShowACHPaymentMethod,
}: GetPaymentMethodOptionsArgs): ReactElement<typeof PaymentMethodPanel> | undefined => {
  if (paymentMethod.type === PaymentMethodType.CARD) {
    const creditCard = paymentMethod as CreditCard;

    // convert expMonth & expYear to Numbers
    const expMonth = +(creditCard.card?.expirationMonth ?? 0);
    const expYear = +(creditCard.card?.expirationYear ?? 0);
    const card = {
      brand: creditCard.card?.brand ?? '',
      expMonth,
      expYear,
      last4: creditCard.card?.last4Numbers ?? '',
      name: creditCard.card?.cardHolderName ?? '',
    };

    const RadioButton = (
      <Radio
        name={creditCard.id as string}
        isChecked={creditCard.id === selectedPaymentMethodId}
        onChange={() => {
          isCreditManagementEnabled && setShowTermPaymentWarning && setShowTermPaymentWarning(false);
          selectPaymentMethod(paymentMethod.id as string);
        }}
      />
    );

    const CardPanel = (
      <PaymentMethodPanel
        key={`paymethod-${index}`}
        paymentMethod={{
          id: creditCard.id as PaymentMethodId,
          type: paymentMethod.type,
          card,
          default: defaultPaymentMethodId === creditCard.id,
        }}
      />
    );

    if (version === 2) {
      return (
        <Flex alignItems="center" gap="space.150" key={`payment-method-${index}`}>
          {RadioButton}
          <div
            className={classNames('payment-method-panel-container-v2', {
              'is-default': defaultPaymentMethodId === creditCard.id,
            })}
          >
            {CardPanel}
          </div>
        </Flex>
      );
    }

    return (
      <div className="payment-method-panel-container">
        {RadioButton}
        {CardPanel}
        {defaultPaymentMethodId === creditCard.id && (
          <Lozenge appearance="default" testId={`default-status-lozenge-${creditCard.id}`}>
            Default
          </Lozenge>
        )}
      </div>
    );
  } else if (isCreditManagementEnabled && paymentMethod.type === PaymentMethodType.DEFERRED) {
    const paymentOnTerms = paymentMethod as generatedTypesNew.PaymentOnTerms;
    const hasPayByStatement = paymentOnTerms?.terms?.payOnStatement;
    const RadioButton = (
      <Radio
        name={paymentOnTerms.id as string}
        isChecked={isTermPaymentAvailable && paymentOnTerms.id === selectedPaymentMethodId}
        isDisabled={!isTermPaymentAvailable || termPaymentStatus === txaPushPaymentStatus.INELIGIBLE}
        onChange={() => {
          if (isTermPaymentAvailable) {
            if (setShowTermPaymentWarning && isDefaultMethodIsCard) {
              setShowTermPaymentWarning(true);
            }
            selectPaymentMethod(paymentMethod.id as string);
          }
        }}
        testId="term-payment-method"
      />
    );

    if (version === 2) {
      return (
        <Flex alignItems="center" gap="space.150" key={`payment-method-${index}`}>
          {RadioButton}
          <div className="term-payment-container-v2">
            <TermPaymentMethod
              paymentNetType={hasPayByStatement ? '' : (paymentOnTerms?.paymentMethod as string)}
              totalLimit={hasPayByStatement ? undefined : (paymentOnTerms?.totalCreditLimit as number)}
              currentTermAccountStatus={termPaymentStatus}
              hasPayOnAccount={!!hasPayByStatement}
            />
          </div>
        </Flex>
      );
    }

    return (
      <div className="payment-method-panel-container">
        {RadioButton}
        <TermPaymentMethod
          paymentNetType={hasPayByStatement ? '' : (paymentOnTerms?.paymentMethod as string)}
          totalLimit={hasPayByStatement ? undefined : (paymentOnTerms?.totalCreditLimit as number)}
          currentTermAccountStatus={termPaymentStatus}
          hasPayOnAccount={!!hasPayByStatement}
        />
      </div>
    );
    //@ts-ignore
  } else if (paymentMethod.type === PaymentMethodTypeV2.PAYPAL) {
    //@ts-ignore
    const emailID = paymentMethod?.payPal?.email;
    const RadioButton = (
      <Radio
        name={paymentMethod.id as string}
        isChecked={paymentMethod.id === selectedPaymentMethodId}
        onChange={() => selectPaymentMethod(paymentMethod.id as string)}
        testId="paypal-payment-method"
      />
    );

    if (version === 2) {
      return (
        <Flex alignItems="center" gap="space.150" key={`payment-method-${index}`}>
          {RadioButton}
          <PaypalPaymentMethod emailID={emailID} isDesignIteration1Enabled={true} iconHeight={40} iconWidth={60} />
        </Flex>
      );
    }

    return (
      <div className="payment-method-panel-container">
        {RadioButton}
        <PaypalPaymentMethod emailID={emailID} />
      </div>
    );
  } else if (shouldShowACHPaymentMethod && isAchPaymentMethod(paymentMethod?.type)) {
    const achPaymentMethod = paymentMethod as Ach;
    const RadioButton = (
      <Radio
        name={paymentMethod.id as string}
        isChecked={paymentMethod.id === selectedPaymentMethodId}
        onChange={() => selectPaymentMethod(paymentMethod.id as string)}
        testId="paypal-payment-method"
      />
    );

    if (version === 2) {
      return (
        <Flex alignItems="center" gap="space.150" key={`payment-method-${index}`}>
          {RadioButton}
          <AchPanel
            bankName={achPaymentMethod.ach?.bankName}
            last4={achPaymentMethod.ach?.last4}
            showBorder
            showInfoInline={false}
          />
        </Flex>
      );
    }

    return (
      <div className="payment-method-panel-container">
        {RadioButton}
        <AchPanel
          bankName={achPaymentMethod.ach?.bankName}
          last4={achPaymentMethod.ach?.last4}
          showBorder
          showInfoInline={false}
        />
      </div>
    );
  }
  return;
};

const wrapInRowItem = (node: ReactElement | undefined | null | boolean) => {
  return <div className="row-item">{node}</div>;
};

const PaymentMethodsTableV2 = ({
  cardPaymentMethods,
  defaultPaymentMethodId,
  selectPaymentMethod,
  selectedPaymentMethodId,
  header = 'Payment methods',
  setShowTermPaymentWarning,
  isCreditManagementEnabled = false,
  termPaymentMethods,
  isTermPaymentAvailable = false,
  isDefaultMethodIsCard = false,
  termPaymentStatus,
  version,
  payPalPaymentMethods,
  isPaypalSupportEnabled,
  showHeader = false,
  shouldShowACHPaymentMethod,
  achPaymentMethods,
}: Props) => {
  const isCardPaymentMethodsAvailable = Boolean(cardPaymentMethods?.length);
  const isTermPaymentMethodsAvailable = Boolean(termPaymentMethods?.length);
  const isPayPalPaymentMethodsAvailable = Boolean(payPalPaymentMethods?.length);
  const isACHPaymentMethodAvailable = shouldShowACHPaymentMethod && Boolean(achPaymentMethods?.length);

  // this will arrange the default payment method on top of other card payment methods
  const prioritizeDefaultPaymentMethod = (paymentMethods: any[]): any[] => {
    return paymentMethods?.reduce((acc, method) => {
      return method?.id === defaultPaymentMethodId ? [method, ...acc] : [...acc, method];
    }, []);
  };

  const renderCardPaymentMethods = () =>
    isCardPaymentMethodsAvailable &&
    prioritizeDefaultPaymentMethod(cardPaymentMethods as PaymentMethod[]).map((paymentMethod, index) => {
      if (paymentMethod.type !== PaymentMethodType.CARD) return null;
      const paymentMethodOption = getPaymentMethodOption({
        version,
        paymentMethod,
        index,
        defaultPaymentMethodId,
        selectPaymentMethod,
        selectedPaymentMethodId,
        setShowTermPaymentWarning,
        isCreditManagementEnabled,
      });
      if (version === 2) {
        return paymentMethodOption;
      }

      return (
        <div className="payment-method-row" key={`payment-method-${index}`}>
          {wrapInRowItem(paymentMethodOption)}
        </div>
      );
    });

  const renderTermPaymentMethods = () =>
    isTermPaymentMethodsAvailable &&
    prioritizeDefaultPaymentMethod(termPaymentMethods as generatedTypesNew.PaymentOnTerms[])?.map(
      (paymentMethod, index) => {
        if (paymentMethod.type !== PaymentMethodType.DEFERRED) return null;

        const paymentMethodOption = getPaymentMethodOption({
          version,
          paymentMethod,
          index,
          defaultPaymentMethodId,
          selectPaymentMethod,
          selectedPaymentMethodId,
          setShowTermPaymentWarning,
          isCreditManagementEnabled,
          isTermPaymentAvailable,
          isDefaultMethodIsCard,
          termPaymentStatus,
        });

        if (version === 2) {
          return paymentMethodOption;
        }
        return (
          <div className="payment-method-row" key={`term-payment-method-${index}`}>
            {wrapInRowItem(paymentMethodOption)}
          </div>
        );
      }
    );

  const renderPaypalPaymentMethod = () =>
    prioritizeDefaultPaymentMethod(payPalPaymentMethods as any)?.map((paymentMethod, index) => {
      if (paymentMethod.type !== PaymentMethodTypeV2.PAYPAL) return null;

      const paymentMethodOption = getPaymentMethodOption({
        version,
        paymentMethod,
        index,
        defaultPaymentMethodId,
        selectPaymentMethod,
        selectedPaymentMethodId,
      });

      if (version === 2) {
        return paymentMethodOption;
      }
      return (
        <div className="payment-method-row" key={`term-payment-method-${index}`}>
          {wrapInRowItem(paymentMethodOption)}
        </div>
      );
    });

  const renderACHPaymentMethod = () => {
    return prioritizeDefaultPaymentMethod(achPaymentMethods as Ach[])?.map((paymentMethod, index) => {
      if (!isAchPaymentMethod(paymentMethod?.type)) return null;

      const paymentMethodOption = getPaymentMethodOption({
        version,
        paymentMethod,
        index,
        defaultPaymentMethodId,
        selectPaymentMethod,
        selectedPaymentMethodId,
        shouldShowACHPaymentMethod,
      });

      if (version === 2) {
        return paymentMethodOption;
      }

      return (
        <div className="payment-method-row" key={`ach-payment-method-${index}`}>
          {wrapInRowItem(paymentMethodOption)}
        </div>
      );
    });
  };

  const renderBody = () => {
    return (
      <>
        {renderTermPaymentMethods()}
        {renderCardPaymentMethods()}
        {isPaypalSupportEnabled && renderPaypalPaymentMethod()}
        {shouldShowACHPaymentMethod && renderACHPaymentMethod()}
        {((!isCreditManagementEnabled && !isCardPaymentMethodsAvailable) ||
          (isCreditManagementEnabled &&
            !isCardPaymentMethodsAvailable &&
            !isTermPaymentMethodsAvailable &&
            !isPayPalPaymentMethodsAvailable &&
            !isACHPaymentMethodAvailable)) && <EmptyState header="We couldn't find any payment methods"></EmptyState>}
      </>
    );
  };

  if (version === 2) {
    return (
      <Box paddingBlock="space.200" testId="payment-methods-v2">
        <Flex direction="column" gap="space.300">
          {showHeader && (
            <Text color="color.text.subtle" size="small" weight="semibold">
              {header}
            </Text>
          )}
          {renderBody()}
        </Flex>
      </Box>
    );
  }

  return (
    <div className="payment-methods-container" data-testid="payment-methods-table-v2">
      <div className="payment-methods-table">
        <p className="payment-methods-header">{header}</p>
        {renderBody()}
      </div>
    </div>
  );
};

export default PaymentMethodsTableV2;
